import { useCallback, useEffect, useMemo, useState } from 'react'
import {
	type EventType,
	type EmptyProfile,
	type Profile,
	useEventTypesListEventTypes,
	useMeProfileGetMeProfile,
	useMeProfileUpdateMeProfile,
	getEventTypesListEventTypesQueryKey,
	getMeProfileGetMeProfileQueryKey,
} from '@cocoonspace/api'
import { Button } from '@cocoonspace/ui/components/button'
import { DividerLabel } from '@cocoonspace/ui/components/cocoon/divider-label'
import { SortableList } from '@cocoonspace/ui/components/cocoon/sortable-list'
import { useLingui } from '@lingui/react/macro'
import { useQueryClient } from '@tanstack/react-query'
import { PlusIcon } from 'lucide-react'
import { toast } from 'sonner'

import { ErrorMsg } from '~/components/error-msg'
import { CreateEventTypeAction } from './create-event-type-action'
import {
	EventTypeCard,
	EventTypeCardEdition,
	EventTypeCardHandle,
} from './event-types/event_type_card'
import { EventTypeCardSkeletonList } from './event-types/event_type_card/skeleton'
import { NoEventTypes } from './no-event-types'

const splitEventTypesByVisibility = ({
	availableEventTypes,
	publicEventTypeIds,
}: {
	availableEventTypes: EventType[]
	publicEventTypeIds: string[]
}) => {
	const byId = new Map(availableEventTypes.map((e) => [e.id, e]))
	const publicIdsSet = new Set(publicEventTypeIds)

	const publicEventTypes = publicEventTypeIds
		.map((id) => byId.get(id))
		.filter((e): e is EventType => !!e)

	const privateEventTypes = availableEventTypes.filter(
		({ id }) => !publicIdsSet.has(id),
	)

	return { publicEventTypes, privateEventTypes }
}

const EventTypesError = () => {
	const { t } = useLingui()

	return (
		<ErrorMsg
			message={t`Une erreur est survenue lors de la récupération des types d'événements`}
		/>
	)
}

const isProfile = (profile: EmptyProfile | Profile): profile is Profile =>
	!('isEmpty' in profile)

export const EventTypesList = () => {
	const { t } = useLingui()
	const queryClient = useQueryClient()

	const { data: meProfile } = useMeProfileGetMeProfile()
	const { mutateAsync: updateMeProfile } = useMeProfileUpdateMeProfile()
	const { data: eventTypes, isLoading, isError } = useEventTypesListEventTypes()

	const serverPublicEventTypeIds = useMemo(() => {
		if (!meProfile || !isProfile(meProfile)) return []
		return meProfile.eventTypeIds ?? []
	}, [meProfile])

	const [optimisticPublicEventTypeIds, setOptimisticPublicEventTypeIds] =
		useState<string[] | null>(null)

	useEffect(() => {
		setOptimisticPublicEventTypeIds(serverPublicEventTypeIds)
	}, [serverPublicEventTypeIds])

	const publicEventTypeIds =
		optimisticPublicEventTypeIds ?? serverPublicEventTypeIds

	const availableEventTypes = useMemo(
		() => eventTypes?.data ?? [],
		[eventTypes],
	)

	const { publicEventTypes, privateEventTypes } = useMemo(
		() =>
			splitEventTypesByVisibility({
				availableEventTypes,
				publicEventTypeIds,
			}),
		[availableEventTypes, publicEventTypeIds],
	)

	const invalidateQueries = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: getMeProfileGetMeProfileQueryKey(),
		})
		queryClient.invalidateQueries({
			queryKey: getEventTypesListEventTypesQueryKey(),
		})
	}, [queryClient])

	const onChange = useCallback(
		(next: EventType[]) => {
			const eventTypeIds = next.map(({ id }) => id)
			setOptimisticPublicEventTypeIds(eventTypeIds)

			// Optimistically reorder the profile ids cache.
			queryClient.setQueryData(getMeProfileGetMeProfileQueryKey(), (prev) => {
				if (!prev || typeof prev !== 'object') return prev
				const p = prev as { data?: unknown } & Record<string, unknown>

				if (p.data && typeof p.data === 'object') {
					return {
						...p,
						data: { ...(p.data as Record<string, unknown>), eventTypeIds },
					}
				}

				return { ...p, eventTypeIds }
			})

			updateMeProfile({ data: { eventTypeIds } })
				.then(() => {
					toast.success(t`Ordre des types de rendez-vous mis à jour`)
					invalidateQueries()
				})
				.catch(() => {
					toast.error(t`Une erreur est survenue`)
					setOptimisticPublicEventTypeIds(serverPublicEventTypeIds)
					invalidateQueries()
				})
		},
		[
			queryClient,
			updateMeProfile,
			invalidateQueries,
			t,
			serverPublicEventTypeIds,
		],
	)

	if (isLoading) return <EventTypeCardSkeletonList />
	if (isError) return <EventTypesError />
	if (availableEventTypes.length === 0) return <NoEventTypes />

	return (
		<div className='flex flex-col gap-4'>
			<h2 className='flex items-center gap-4 text-lg font-bold'>
				<span className='flex-1'>{t`Types de rendez-vous`}</span>
				<CreateEventTypeAction>
					<Button size='sm'>
						<PlusIcon />
						{t`Créer`}
					</Button>
				</CreateEventTypeAction>
			</h2>

			<div className='flex flex-col gap-2'>
				<DividerLabel>{t`Visibles sur votre page publique`}</DividerLabel>

				<SortableList
					getId={({ id }) => id}
					value={publicEventTypes}
					onChange={onChange}
					renderItem={(eventType, { dragHandleProps }) => (
						<EventTypeCard
							eventType={eventType}
							dragHandleProps={dragHandleProps}
							prepend={() => (
								<EventTypeCardHandle dragHandleProps={dragHandleProps} />
							)}
							append={(eventType) => (
								<EventTypeCardEdition
									eventType={eventType}
									isPublic
								/>
							)}
						/>
					)}
				/>
			</div>

			<div className='flex flex-col gap-2'>
				<DividerLabel>{t`Privés`}</DividerLabel>

				{privateEventTypes.map((eventType) => (
					<EventTypeCard
						key={eventType.id}
						eventType={eventType}
						append={(eventType) => (
							<EventTypeCardEdition eventType={eventType} />
						)}
					/>
				))}
			</div>
		</div>
	)
}
